import React, { createContext, useContext, useMemo } from 'react';
import { USER_TYPES } from '../pages/users-managment/constants';
import { useAuth0, User } from '@auth0/auth0-react';

export type PermissionType =
  | 'admin'
  | 'active'
  | 'export'
  | 'financials'
  | 'edit'
  | 'approver';

type UserProviderProps = {
  userData: {
    roles: {
      generalPermissions: {
        admin: boolean;
        active: boolean;
        export: boolean;
        financials: boolean;
        edit: boolean;
        approver: boolean;
      };
      selectedValue: string | null;
      selectedTags: string[] | null;
      __typename: string;
    };
  };
  children: React.ReactNode;
};

const initialUserContext = {
  isAdmin: false,
  isActive: false,
  canExport: false,
  canViewFinancials: false,
  canEdit: false,
  canApprove: false,
  isCorporate: false,
  isDivision: false,
  isLocation: false,
  isSuperAdmin: false,
  selectedTags: null as string[] | null,
  currentRoles: null as UserProviderProps['userData']['roles'] | null,
};

const UserContext = createContext(initialUserContext);

const SUPER_ADMIN = 'admin';

type UserType = User & {
  userRoles: {
    name: string;
    description: string;
    id: string;
  }[];
};

export const UserProvider = ({ children, userData }: UserProviderProps) => {
  const { user } = useAuth0();

  const roles = useMemo(
    () => (user as UserType)?.userRoles?.map((role) => role.name) || [],
    [user],
  );

  const isSuperAdmin = useMemo(() => roles.includes(SUPER_ADMIN), [roles]);

  const value = useMemo(() => {
    if (!userData.roles) return initialUserContext;
    return {
      isAdmin: userData.roles.generalPermissions.admin,
      isActive: userData.roles.generalPermissions.active,
      canExport: userData.roles.generalPermissions.export,
      canViewFinancials: userData.roles.generalPermissions.financials,
      canEdit: userData.roles.generalPermissions.edit,
      canApprove: userData.roles.generalPermissions.approver,
      isCorporate:
        userData.roles.selectedValue === USER_TYPES.CORPORATE.toUpperCase(),
      isDivision:
        userData.roles.selectedValue === USER_TYPES.DIVISION.toUpperCase(),
      isLocation:
        userData.roles.selectedValue === USER_TYPES.LOCATION.toUpperCase(),
      selectedTags: userData.roles.selectedTags,
      currentRoles: userData.roles,
      isSuperAdmin,
    };
  }, [user, userData]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUserContext = () => useContext(UserContext);
